<template lang="pug">
div(class="w-full")
    div(class="relative w-full z-30")
        p(class="")
            input(
                ref="searchInput"
                id="searchBar"
                type='text'
                :placeholder="searchPlaceholder"
                @focus="openSearch"
                @keyup.esc="closeSearch();clearSearch()"
                @keyup.enter="submitSearch"
                @input="trackInput"
                v-model="searchQuery"
                autocomplete="off"
                aria-label="Search"
                data-testid="searchBar"
                class="w-full bg-white rounded-sm border px-3 text text-sm"
                :class="{'focus-glow': searchIsGlowing}"
                style="height: 45px;"
            )
            span(class="absolute top-0 right-0 h-10 w-10 inline-flex items-center justify-center text-right pointer-events-auto z-0")
                SearchIcon(
                    v-if="$device.isDesktop"
                    @click="submitSearch"
                    class="w-4 h-4"
                )
                CloseIcon(
                    v-if="showCloseIcon && !$device.isDesktop"
                    @click="closeSearch();clearSearch()"
                    class="w-4 h-4"
                )

        SearchResults(
            v-if="searchIsActive"
            :searchQuery="searchQuery"
            @close="closeSearch();clearSearch()"
        )

    // This is a transparent shade to catch the clicks off of the search bar
    div(
        v-if="searchIsActive"
        @click="closeSearch();clearSearch()"
        class="fixed top-0 right-0 bottom-0 left-0 z-20"
    )

</template>

<script>
import getRefittedUrlMixin from '@/helpers/getRefittedUrlMixin'
const SearchResults = () =>
    import(/* webpackChunkName: "search-bar-results" */ './_components/searchResults')
const SearchIcon = () =>
    import(/* webpackChunkName: "search-icon" */ '@/assets/common/search?inline')
const CloseIcon = () => import(/* webpackChunkName: "close-icon" */ '@/assets/common/x?inline')

export default {
    name: 'SearchBar',
    components: {
        SearchResults,
        SearchIcon,
        CloseIcon,
    },
    mixins: [getRefittedUrlMixin],
    data() {
        return {
            searchQuery: '',
            searchIsActive: false,
            showCloseIcon: false,
            inputTimer: null,
        }
    },
    computed: {
        fitmentFormatted() {
            return this.$store.getters['fitment/fitmentFormatted']
        },
        searchIsGlowing() {
            return this.$store.state.is404
        },
        searchPlaceholder() {
            if (!this.fitmentFormatted) return this.$config.runtime.theme.search.searchText
            return `Search ${this.fitmentFormatted} Parts`
        },
    },
    watch: {
        $route() {
            if (this.searchIsActive) {
                this.closeSearch()
                if (this.$route.name !== 'search') this.clearSearch()
            }
        },
    },
    methods: {
        emitSearched() {
            if (!this.searchQuery) return
            this.$uiEvents.$emit('searched', { query: this.searchQuery })
        },
        trackInput() {
            if (this.inputTimer) clearTimeout(this.inputTimer)
            this.inputTimer = setTimeout(() => {
                this.emitSearched()
            }, 2000)
        },
        async submitSearch() {
            if (!this.searchQuery) return
            if (this.inputTimer) clearTimeout(this.inputTimer)
            if (this.$route.query.q === this.searchQuery.trim()) this.closeSearch()
            this.emitSearched()

            if (this.$config.runtime.theme.search.searchRedirects.enabled) {
                // Check for a Redirect attached to this search query
                const { results } = await this.$search.searchRedirect
                    .setQuery(`"${this.searchQuery.trim()}"`)
                    ._search()

                const [result] = results

                // If we find a Redirect match for the Query we need to push them to that
                if (result && result.terms.includes(this.searchQuery.trim().toLowerCase())) {
                    // Handle External Link
                    if (
                        result.url.startsWith('http') &&
                        !result.url.startsWith(`https://${this.$config.runtime.domain}`)
                    ) {
                        window.location = result.url
                        return
                    }

                    // Handle Internal Link
                    this.$push.to(this.getRefittedUrl(result.url))
                    return
                }
            }

            this.$push.to({ name: 'search', query: { q: this.searchQuery.trim() } })
        },
        openSearch() {
            this.showCloseIcon = true
            this.searchIsActive = true
            this.$emit('searchIsActive', true)
        },
        closeSearch() {
            this.showCloseIcon = false
            this.searchIsActive = false
            this.$emit('searchIsActive', false)
            // in case they pressed escape
            this.$refs.searchInput.blur()
        },
        clearSearch() {
            this.searchQuery = ''
        },
    },
}
</script>

<style lang="stylus" scoped>
input::placeholder
    color: $textDefault
    +desktop-only()
        font-size: $smallFontSize
.focus-glow
    box-shadow 0 0 0 2px $info
    animation glow 500ms linear 0s infinite alternate
@keyframes glow
    from
        box-shadow 0 0 0 2px $info
    to
        box-shadow 0 0 0 3px $info
</style>
